import { contractingCaches, estimatePayloadCaches, gcgrCaches } from "../api";
import { costModelCaches } from "../api/costModels";
import { CacheHandle, clearTogether } from "../api/helpers";
import { clearAllDataTables } from "./reports/dataTables";
import { clearProcessedEstimateCache, estimateLookupCaches } from "./datasources/estimate";
import { CARD_SUMMARY_CACHE_KEYS, forgetCardSummaries } from "./datasources/cardSummaries";
import {
  REPORT_METADATA_TABLES,
  clearEstimateReportTable,
  clearReportTables,
  syncLegacyReportTable,
} from "./datasources/reportTables";
import { loadedDatasourceStore } from "../utils/storageKeys";
import { workbookCache } from "./workbook/cache";
import { LEGACY_CARD_CACHE_KEYS, RETIRED_CACHE_KEYS } from "./workbook/cacheKeys";
import { refillPrintLayouts } from "./workbook/printLayout";

const projectCaches: CacheHandle[] = [
  ...estimatePayloadCaches,
  ...estimateLookupCaches,
  ...contractingCaches,
  ...gcgrCaches,
  ...costModelCaches,
];

/**
 * Drops the workbook's project-scoped data: the `.metadata` report tables, the saved card summaries
 * and every cache keyed to the project. Shared by a project switch and a project removal,
 * which invalidate the same things — a switch then re-tabulates, a removal does not.
 */
export const clearProjectData = async (): Promise<void> => {
  clearProjectCaches();
  await clearReportTables(...REPORT_METADATA_TABLES);
  loadedDatasourceStore.clear();
  // A project switch leaves none of them valid, and a removal wants them all.
  await clearAllDataTables();
  forgetCardSummaries();
  await workbookCache.write({ delete: [...CARD_SUMMARY_CACHE_KEYS, ...LEGACY_CARD_CACHE_KEYS] });
  await refillPrintLayouts();
};

/** Drops every cache keyed to the project, leaving the workbook alone. */
export const clearProjectCaches = (): void => {
  clearTogether(projectCaches);
  clearProcessedEstimateCache();
};

export const clearEstimateSelection = async (): Promise<void> => {
  clearTogether([...estimatePayloadCaches, ...estimateLookupCaches]);
  clearProcessedEstimateCache();
  await clearEstimateReportTable();
  // clearEstimateReportTable also emptied the legacy combined __Report table; repopulate it from
  // the project row (estimate columns blanked) so old workbook formulas keep resolving. No-op
  // unless the workbook has __Report.
  await syncLegacyReportTable();
};

export const retireLegacyCacheKeys = (): Promise<void> =>
  workbookCache.write({
    delete: RETIRED_CACHE_KEYS.filter((key) => !(LEGACY_CARD_CACHE_KEYS as readonly string[]).includes(key)),
  });
